import { useEffect, useState } from 'react';
import api from '@/utils/api';
import { Country, City } from '../types';

export const useLocation = (initialCountry?: string) => {
    const [countries, setCountries] = useState<Country[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');

    // Fetch countries on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await api.get('/location/countries');
                setCountries(response.data);
            } catch (err) {
                console.error('Failed to fetch countries:', err);
            }
        };
        fetchCountries();
    }, []);

    // Update selected country code when initialCountry changes or countries load
    useEffect(() => {
        if (initialCountry && countries.length > 0) {
            const country = countries.find(c => c.name === initialCountry || c.code === initialCountry);
            if (country) {
                setSelectedCountryCode(country.code);
            }
        }
    }, [initialCountry, countries]);

    // Fetch cities when country code changes
    useEffect(() => {
        const fetchCities = async () => {
            if (!selectedCountryCode) {
                setCities([]);
                return;
            }

            setIsLoadingCities(true);
            try {
                const response = await api.get(`/location/countries/${selectedCountryCode}/cities`);
                setCities(response.data);
            } catch (err) {
                console.error('Failed to fetch cities:', err);
                setCities([]);
            } finally {
                setIsLoadingCities(false);
            }
        };

        fetchCities();
    }, [selectedCountryCode]);

    const handleCountryChange = (countryNameOrCode: string) => {
        const country = countries.find(c => c.name === countryNameOrCode || c.code === countryNameOrCode);
        if (country) {
            setSelectedCountryCode(country.code);
        } else {
            setSelectedCountryCode('');
        }
    };

    return {
        countries,
        cities,
        isLoadingCities,
        selectedCountryCode,
        handleCountryChange
    };
};
